import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, User, UtensilsCrossed, Wine, PartyPopper, Music } from 'lucide-react';
import { PageHeader } from '../components/PageHeader';
import { useLanguage } from '../components/LanguageContext';
const SPEAKER_PHOTOS = ['https://cdn.magicpatterns.com/uploads/bw2DnyM6FmhKq8Tom4A8PK/Screenshot_2026-04-22_alle_14.08.09.png', 'https://cdn.magicpatterns.com/uploads/aXm9zUaw7Zgc93hDNUKHT4/Screenshot_2026-04-22_alle_14.07.54.png', 'https://cdn.magicpatterns.com/uploads/4d3qW4vZHbhTCP7TFhFLea/Screenshot_2026-04-22_alle_14.07.40.png', 'https://cdn.magicpatterns.com/uploads/bEsp2sbrpANdQoJZXBAUh9/Screenshot_2026-04-22_alle_14.07.34.png', 'https://cdn.magicpatterns.com/uploads/a2y8aUgNhv3KufdXF2MAs6/Screenshot_2026-04-22_alle_14.07.27.png'];
type EventType = 'KEYNOTE' | 'PANEL' | 'RESEARCH' | 'WORKSHOP' | 'IDEATHON' | 'MUSIC' | 'BREAK' | 'CLOSING' | 'OPENING';
type TbcStatus = 'tbc' | 'tbd' | null;
interface Speaker {
  name: string;
  role: string;
  tbc?: TbcStatus;
}
interface Event {
  id: string;
  time: string;
  title: string;
  type: EventType;
  speakers?: Speaker[];
  moderator?: string;
  moderatorTbc?: TbcStatus;
  performer?: string;
}
// Strip any trailing [TBC]/[TBD] marker from a name/role string
function stripTbcMarker(s: string): {
  clean: string;
  tbc: TbcStatus;
} {
  const tbcMatch = s.match(/\s*\[TBC\]\s*$/i);
  if (tbcMatch) return {
    clean: s.replace(tbcMatch[0], '').trim(),
    tbc: 'tbc'
  };
  const tbdMatch = s.match(/\s*\[TBD\]\s*$/i);
  if (tbdMatch) return {
    clean: s.replace(tbdMatch[0], '').trim(),
    tbc: 'tbd'
  };
  // Also handle "Speaker [TBD]" / "Representative [TBD]" name patterns — treat whole name as tbd
  if (/^(speaker|representative|participant representative)\s*\[tbd\]$/i.test(s.trim())) {
    return {
      clean: s.replace(/\s*\[tbd\]\s*$/i, '').trim(),
      tbc: 'tbd'
    };
  }
  return {
    clean: s,
    tbc: null
  };
}
// Strip leading type prefix (e.g. "KEYNOTE: ", "Panel 1: ", "Panel 3 [ITA]: ",
// "Research: ", "Workshop: ", "Ideathon: ", "Closing Remarks: ") from event titles.
function stripTypePrefix(title: string): string {
  return title.replace(/^(KEYNOTE|Panel\s+\d+(?:\s*\[[^\]]+\])?|Research|Workshop|Ideathon|Closing Remarks)\s*:\s*/i, '');
}
const FRIDAY_EVENTS: Event[] = [{
  id: 'f1',
  time: '09:30',
  title: 'Openings',
  type: 'OPENING',
  speakers: [{
    name: 'Andrea Geremicca',
    role: 'Director General, European Institute of Innovation for Sustainability (EIIS)'
  }, {
    name: 'H.E. Miguel Angel Moratinos',
    role: 'Under-Secretary-General, High Representative, UNAOC'
  }, {
    name: 'Roberto Gualtieri',
    role: 'Mayor of Rome'
  }],
  moderator: 'Lorenzo Micheli'
}, {
  id: 'f2',
  time: '09:55',
  title: 'Institutional Greetings',
  type: 'OPENING',
  speakers: [{
    name: 'Francesco Lollobrigida',
    role: 'Minister of Agriculture, Food Sovereignty and Forests, Italian Republic'
  }],
  moderator: 'Lorenzo Micheli'
}, {
  id: 'f3',
  time: '10:10',
  title: 'KEYNOTE: Trust, Brands and the Architecture of the Future Economy',
  type: 'KEYNOTE',
  speakers: [{
    name: 'Raja Rajamannar',
    role: 'Senior Fellow, Former CMCO, Mastercard'
  }]
}, {
  id: 'f4',
  time: '10:30',
  title: 'KEYNOTE: Reimagining the Future of the SDGs',
  type: 'KEYNOTE',
  speakers: [{
    name: 'Jeffrey Sachs',
    role: 'President, Sustainable Development Solutions Network'
  }]
}, {
  id: 'f5',
  time: '10:50',
  title: 'Panel 1: The Next Infrastructure Systems',
  type: 'PANEL',
  speakers: [{
    name: 'Gianbattista La Rocca',
    role: 'CEO, Italo S.p.A.'
  }, {
    name: 'Eugenie L. Birch',
    role: 'Co-Director, Penn Institute for Urban Research'
  }, {
    name: 'Nicoletta Russo',
    role: 'Head of IR & Sustainability, Ferrari'
  }, {
    name: 'Andrea Rocco',
    role: 'Chief Sustainability & Risk Officer, Brembo'
  }, {
    name: 'Marek Reichman',
    role: 'Executive Vice President & CCO, Aston Martin'
  }, {
    name: 'Patrick Zhong',
    role: 'Former Head of Global Investments, Fosun Group'
  }],
  moderator: 'Martina Schiuma',
  moderatorTbc: 'tbc'
}, {
  id: 'f6',
  time: '11:35',
  title: 'Panel 2: AI as the Backbone of the Future for a Human-centred Economy',
  type: 'PANEL',
  speakers: [{
    name: 'Maximo Ibarra',
    role: 'Former CEO, Engineering'
  }, {
    name: 'Alessandra Poggiani',
    role: 'General Director, Cineca'
  }, {
    name: 'Carlo Tortora Brayda di Belvedere',
    role: 'CEO, Cyber Eagle'
  }, {
    name: 'Giorgio Metta',
    role: 'Scientific Director, IIT'
  }],
  moderator: 'Camila Kakabadse'
}, {
  id: 'f7',
  time: '12:05',
  title: 'Panel 3 [ITA]: Where Regenerative Industry Is Emerging – Part 1',
  type: 'PANEL',
  speakers: [{
    name: 'Mauro Fontana',
    role: 'President, Mutti S.p.A.'
  }, {
    name: 'Maura Latini',
    role: 'President, COOP'
  }, {
    name: 'Fabrizio Gavelli',
    role: 'President & General Manager, Ferrero Italy'
  }, {
    name: 'Lara Ponti',
    role: 'Vice President, Confindustria'
  }],
  moderator: 'Stefania Iodice'
}, {
  id: 'f8',
  time: '12:35',
  title: 'Panel 3 [ITA]: Where Regenerative Industry Is Emerging – Part 2',
  type: 'PANEL',
  speakers: [{
    name: 'Matthias Gasser',
    role: 'CEO, Menz&Gasser'
  }, {
    name: 'Simona Fontana',
    role: 'General Director, CONAI'
  }, {
    name: 'Alessandra Prampolini',
    role: 'General Director, WWF Italy'
  }, {
    name: 'Marco Beghini',
    role: 'Global Sustainability Director, Barilla Group'
  }],
  moderator: 'Anna Ruini'
}, {
  id: 'f9',
  time: '13:00',
  title: 'Lunch Break',
  type: 'BREAK'
}, {
  id: 'f10',
  time: '14:35',
  title: 'Panel 4: Signals of Next-Generation Leadership',
  type: 'PANEL',
  speakers: [{
    name: 'Daniela Fatarella',
    role: 'CEO, Save the Children Italy'
  }, {
    name: 'Patrizia Lombardi',
    role: 'Professor, Polytechnic University of Turin'
  }, {
    name: 'Phoebe Koundouri',
    role: 'Professor, Athens University & University of Cambridge'
  }, {
    name: 'Jacqueline Corbelli',
    role: 'CEO, BrightLine'
  }, {
    name: 'Laura De Dilectis',
    role: 'CEO VWH, Vice President DXS, Psychologist'
  }],
  moderator: 'Eugenio Russo'
}, {
  id: 'f11',
  time: '15:10',
  title: 'Panel 5 [ITA]: Feeding the Future of Food',
  type: 'PANEL',
  speakers: [{
    name: 'Laura Marchelli',
    role: 'Group Sustainability Manager, Mutti S.p.A.'
  }, {
    name: 'Giulia Castellucci',
    role: 'Public Affairs Manager, YARA Italia'
  }, {
    name: 'Andrea Campelli',
    role: 'Communications & External Relations Director, COREPLA'
  }, {
    name: 'Elena Sgaravatti',
    role: 'Vice President, Assobiotec-Federchimica'
  }],
  moderator: 'Stefania Iodice'
}, {
  id: 'f12',
  time: '15:40',
  title: 'Panel 6: Rethinking Agrifood Systems',
  type: 'PANEL',
  speakers: [{
    name: 'Ngozi Ifeoma Odiaka',
    role: 'Country Director, UNSDSN Nigeria'
  }, {
    name: 'John Thwaites',
    role: 'Professor, Monash Business School'
  }, {
    name: 'Alain Rival',
    role: 'Professor & Senior Project Manager, CIRAD-UMR ABSys'
  }],
  moderator: 'Sveva Ciapparoni'
}, {
  id: 'f13',
  time: '16:05',
  title: "Research: Rethinking Food Consumers' Choices,",
  type: 'RESEARCH',
  speakers: [{
    name: 'Speaker',
    role: 'Unicamillus',
    tbc: 'tbd'
  }, {
    name: 'Speaker',
    role: 'EIIS',
    tbc: 'tbd'
  }],
  moderator: ''
}, {
  id: 'f14',
  time: '16:20',
  title: 'Panel 7: Financing the Great Transitions',
  type: 'PANEL',
  speakers: [{
    name: 'Francesco Tramontin',
    role: 'Vice President, Global Public Affairs, Ferrero',
    tbc: 'tbc'
  }, {
    name: 'Milena Messori',
    role: 'Head, EIB Group Office Italy'
  }, {
    name: 'Jyotsna Puri',
    role: 'Director, Policy & Programmes, UNEP'
  }, {
    name: 'Gustavo Osvaldo Beliz',
    role: 'Permanent Member, Pontifical Academy of Social Sciences'
  }, {
    name: 'David Donoghue',
    role: 'Former Irish Ambassador'
  }],
  moderator: 'Nardos Berehe'
}, {
  id: 'f15',
  time: '16:55',
  title: 'Music Performance',
  type: 'MUSIC',
  performer: 'Samuele Palumbo, Violinista'
}, {
  id: 'f16',
  time: '17:15',
  title: 'Panel 8: The Ecosystems Where Innovation Happens',
  type: 'PANEL',
  speakers: [{
    name: 'Mehmet Naci Inci',
    role: 'Rector, Boğaziçi University'
  }, {
    name: 'Dina Storey',
    role: 'Executive Director, Expo 2030 Riyadh'
  }, {
    name: 'Muhannad Al Salhi',
    role: 'CEO, Engel & Völkers Italy'
  }, {
    name: 'Diego Raiteri',
    role: 'Vice-president, EIIS'
  }],
  moderator: 'Raquel Armendariz Sucunza'
}, {
  id: 'f17',
  time: '17:45',
  title: 'KEYNOTE: The Future of Global Partnerships',
  type: 'KEYNOTE',
  speakers: [{
    name: 'Juan Lucas Restrepo',
    role: 'Director General, Alliance of Bioversity International & CIAT'
  }]
}, {
  id: 'f18',
  time: '18:05',
  title: 'Panel 9: Moonshots for Development (IFAD)',
  type: 'PANEL',
  speakers: [{
    name: 'Christoph Aubrecht',
    role: 'Programme Coordinator, ESA'
  }, {
    name: 'Representative',
    role: 'IFAD',
    tbc: 'tbd'
  }, {
    name: 'Mara Balestrini',
    role: 'Strategy Advisor, LNET'
  }, {
    name: 'Lisa Finneran',
    role: 'World Bank',
    tbc: 'tbc'
  }, {
    name: 'Representative',
    role: 'EBRD',
    tbc: 'tbd'
  }, {
    name: 'Representative',
    role: 'Agropolis International',
    tbc: 'tbd'
  }],
  moderator: 'Sean Lo Prinzi'
}, {
  id: 'f19',
  time: '18:30',
  title: 'Closing Remarks: Mission-Driven Economies',
  type: 'CLOSING',
  speakers: [{
    name: 'Mariana Mazzucato',
    role: 'Professor in the Economics of Innovation and Public Value, University College London (UCL)',
    tbc: 'tbc'
  }]
}, {
  id: 'f20',
  time: '18:45',
  title: 'Networking Cocktail',
  type: 'BREAK'
}];
const SATURDAY_EVENTS: Event[] = [{
  id: 's1',
  time: '09:40',
  title: 'KEYNOTE: Culture, Education and the Future of Humanity',
  type: 'KEYNOTE',
  speakers: [{
    name: 'Maria Fernanda Espinosa Garces',
    role: 'Former President, UN General Assembly'
  }]
}, {
  id: 's2',
  time: '10:00',
  title: 'Panel 10: Cities, Regions and Universities as Living Laboratories for Climate Action (SDSN)',
  type: 'PANEL',
  speakers: [{
    name: 'Sorin Cheval',
    role: 'Professor, Babeș-Bolyai University Cluj-Napoca & University of Bucharest'
  }, {
    name: 'Amerigo Lombardi',
    role: 'Grant & Project Manager Officer, Icons Foundation'
  }, {
    name: 'Ione Avila-Palencia',
    role: 'Senior researcher, Technical Universidad Politécnica de Madrid'
  }],
  moderator: 'Andrija Erac — Head of Networks, Networks Program'
}, {
  id: 's3',
  time: '10:30',
  title: 'Panel 11: Financing the Future Locally: From FX Risk to Financial System Transformation',
  type: 'PANEL',
  speakers: [{
    name: 'Gulnara Yunusova',
    role: 'Treasurer, IFAD'
  }, {
    name: 'Ruurd Brower',
    role: 'CEO, The Currency Exchange Fund'
  }, {
    name: 'Philip Buyskes',
    role: 'CEO, Frontclear',
    tbc: 'tbc'
  }, {
    name: 'Karina Patrício Ferreira Lima',
    role: 'Senior Fellow, University of Leeds'
  }],
  moderator: 'Sean Lo Prinzi'
}, {
  id: 's4',
  time: '11:00',
  title: 'Workshop: Rethinking Fashion: InsideOut the Next Generation',
  type: 'WORKSHOP',
  speakers: [{
    name: 'Workshop representative',
    role: ''
  }],
  moderator: 'Giusy Bettoni — CEO & Founder, C.L.A.S.S.'
}, {
  id: 's5',
  time: '11:20',
  title: 'Music Performance',
  type: 'MUSIC',
  performer: 'Matteo Fraziano, artista delle ombre'
}, {
  id: 's6',
  time: '11:40',
  title: 'Panel 11: Converging Systems for Nature-Positive Economies',
  type: 'PANEL',
  speakers: [{
    name: 'Andreas Beckmann',
    role: 'Regional CEO, WWF Central and Eastern Europe'
  }, {
    name: 'Fabio Zardini',
    role: 'Country Manager, Patagonia Italy'
  }, {
    name: 'Andrea Cattaneo',
    role: 'Sustainability Impact Controller, H&M Group'
  }, {
    name: 'Chris Heemskerk',
    role: 'CEO & Founder, The Innovation Alliance'
  }, {
    name: 'Luca Ruini',
    role: 'VP Health, Safety, Environment & Energy, Barilla Group'
  }, {
    name: 'André Buldini',
    role: 'General Manager, Six Senses Rome'
  }],
  moderator: 'Petru Capatina'
}, {
  id: 's7',
  time: '12:20',
  title: 'KEYNOTE: What the Next Generations Expect',
  type: 'KEYNOTE',
  speakers: [{
    name: 'Chiara Ferrari',
    role: 'IPSOS'
  }]
}, {
  id: 's8',
  time: '12:40',
  title: 'Ideathon: Ideas from the Next Generation',
  type: 'IDEATHON',
  speakers: [{
    name: 'Participant representative',
    role: 'Ideathon Mobility'
  }, {
    name: 'Participant representative',
    role: 'Ideathon Environment (focus food waste)'
  }],
  moderator: 'Lorenzo Micheli'
}, {
  id: 's9',
  time: '13:00',
  title: 'Panel 12: The Future Seen by the Next Generation',
  type: 'PANEL',
  speakers: [{
    name: 'Speaker',
    role: 'The Jackal',
    tbc: 'tbd'
  }, {
    name: 'Monica Lucarelli',
    role: 'Councillor for Productive Activities and Equal Opportunities, Municipality of Rome'
  }],
  moderator: 'Stefania Iodice'
}, {
  id: 's10',
  time: '13:30',
  title: 'Music Performance',
  type: 'MUSIC',
  performer: 'Noemi Realforte'
}, {
  id: 's11',
  time: '13:45',
  title: 'Closing Ceremony & Best Product',
  type: 'CLOSING'
}];
const getBadgeStyles = (type: EventType) => {
  if (type === 'BREAK') {
    return 'bg-[rgba(197,216,45,0.08)] text-[#888888]';
  }
  return 'bg-[rgba(197,216,45,0.15)] text-[#C5D82D]';
};
interface TbcPillProps {
  tbc: TbcStatus;
}
function TbcPill({
  tbc
}: TbcPillProps) {
  const {
    t
  } = useLanguage();
  if (!tbc) return null;
  const label = tbc === 'tbc' ? t('schedule.tbc') : t('schedule.tbd');
  return <span className="ml-1.5 inline-flex items-center rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wide bg-[#2A2A2A] text-[#AAAAAA] align-middle" title={label} data-id="element-914">
      {tbc.toUpperCase()}
    </span>;
}
interface EventRowProps {
  event: Event;
}
function EventRow({
  event
}: EventRowProps) {
  const {
    t
  } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);
  const hasParticipants = event.speakers && event.speakers.length > 0 || event.moderator;
  const isSpecialRow = event.type === 'BREAK' || event.type === 'MUSIC';
  const participantCount = (event.speakers?.length || 0) + (event.moderator ? 1 : 0);
  const participantsLabel = participantCount === 1 ? t('schedule.participant', {
    n: participantCount
  }) : t('schedule.participants', {
    n: participantCount
  });
  return <div className="border-b border-[#1A1A1A]" data-id="element-915">
      <button onClick={() => hasParticipants && !isSpecialRow && setIsExpanded(!isExpanded)} className="w-full flex gap-4 py-3.5 px-5 text-left hover:bg-[#141414]/30 transition-colors" disabled={!hasParticipants || isSpecialRow} data-id="element-916">
        {/* Time */}
        <div className="w-14 shrink-0 text-sm text-[#888888] tabular-nums" data-id="element-917">
          {event.time}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0" data-id="element-918">
          <h3 className={`text-sm font-medium ${isSpecialRow ? 'text-[#888888] italic' : 'text-white'}`} data-id="element-919">
            {stripTypePrefix(event.title)}
          </h3>
          {event.performer && <p className="text-xs text-[#888888] mt-1" data-id="element-920">{event.performer}</p>}
          {hasParticipants && !isSpecialRow && <div className="flex items-center gap-1.5 mt-1.5" data-id="element-921">
              {isExpanded ? <ChevronUp className="w-3 h-3 text-[#555555]" data-id="element-922" /> : <ChevronDown className="w-3 h-3 text-[#555555]" data-id="element-923" />}
              <span className="text-xs text-[#555555]" data-id="element-924">
                {participantsLabel}
              </span>
            </div>}
        </div>

        {/* Badge */}
        <div className={`rounded-full px-2.5 py-0.5 text-[10px] uppercase font-medium whitespace-nowrap self-start ${getBadgeStyles(event.type)}`} data-id="element-925">
          {event.type}
        </div>
      </button>

      {/* Expanded Content */}
      <AnimatePresence data-id="element-926">
        {isExpanded && hasParticipants && !isSpecialRow && <motion.div initial={{
        height: 0,
        opacity: 0
      }} animate={{
        height: 'auto',
        opacity: 1
      }} exit={{
        height: 0,
        opacity: 0
      }} transition={{
        duration: 0.2,
        ease: 'easeOut'
      }} className="overflow-hidden" data-id="element-927">
            <div className="px-5 pb-3" data-id="element-928">
              <div className="bg-[#141414] rounded-lg p-3" data-id="element-929">
                {/* Speakers */}
                {event.speakers?.map((speaker, index) => <div key={index} className={`flex gap-3 py-2 ${index < (event.speakers?.length || 0) - 1 || event.moderator ? 'border-b border-[#1A1A1A]' : ''}`} data-id="element-930">
                    <img src={SPEAKER_PHOTOS[index % SPEAKER_PHOTOS.length]} alt={speaker.name} className="w-8 h-8 shrink-0 rounded-full object-cover" data-id="element-931" />
                    <div className="flex-1 min-w-0" data-id="element-932">
                      <p className="text-sm font-medium text-white" data-id="element-933">
                        {speaker.name}
                        <TbcPill tbc={speaker.tbc ?? null} data-id="element-934" />
                      </p>
                      {speaker.role && <p className="text-xs text-[#888888] mt-0.5" data-id="element-935">
                          {speaker.role}
                        </p>}
                    </div>
                  </div>)}

                {/* Moderator */}
                {event.moderator && <div className="pt-2" data-id="element-936">
                    <div className="text-[10px] tracking-wider text-[#C5D82D] mb-1 font-medium" data-id="element-937">
                      {t('schedule.moderator')}
                    </div>
                    <div className="flex gap-3 py-2" data-id="element-938">
                      <img src={SPEAKER_PHOTOS[(event.speakers?.length || 0) % SPEAKER_PHOTOS.length]} alt={event.moderator} className="w-8 h-8 shrink-0 rounded-full object-cover" data-id="element-939" />
                      <div className="flex-1 min-w-0" data-id="element-940">
                        <p className="text-sm font-medium text-white" data-id="element-941">
                          {event.moderator}
                          <TbcPill tbc={event.moderatorTbc ?? null} data-id="element-942" />
                        </p>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function SummitHome() {
  const {
    t
  } = useLanguage();
  const [selectedDay, setSelectedDay] = useState<'8' | '9'>('8');
  const events = selectedDay === '8' ? FRIDAY_EVENTS : SATURDAY_EVENTS;
  return <div className="flex flex-col h-full w-full bg-[#0D0D0D] overflow-hidden" data-id="element-943">
      {/* Unified Header */}
      <PageHeader title={t('nav.schedule')} data-id="element-944" />

      {/* Day Tabs */}
      <div className="px-5 flex-shrink-0" data-id="element-945">
        <div className="flex w-full relative" data-id="element-946">
          {[{
          key: '8' as const,
          label: t('schedule.day8')
        }, {
          key: '9' as const,
          label: t('schedule.day9')
        }].map(tab => <button key={tab.key} onClick={() => setSelectedDay(tab.key)} className={`flex-1 py-3 text-[14px] font-medium text-center transition-colors duration-200 relative ${selectedDay === tab.key ? 'text-white' : 'text-[#888888]'}`} data-id="element-947">
              {tab.label}
            </button>)}
          <div className="absolute bottom-0 h-[2px] bg-[#C5D82D] rounded-full transition-all duration-300 ease-out" style={{
          width: '50%',
          left: selectedDay === '8' ? '0%' : '50%'
        }} data-id="element-948" />
        </div>
        <div className="h-px bg-[#2A2A2A]/50" data-id="element-949" />
      </div>

      {/* Schedule List */}
      <div className="flex-1 overflow-y-auto min-h-0" data-id="element-950">
        <div className="pb-24" data-id="element-951">
          {events.map(event => <EventRow key={event.id} event={event} data-id="element-952" />)}
        </div>
      </div>
    </div>;
}